import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Video,
  User,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Loader2,
  ExternalLink,
  Copy,
} from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import {
  getConsultations,
  acceptConsultation,
  rejectConsultation,
  completeConsultation,
} from '../../services/consultationService';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import CustomSelect from '../../components/shared/CustomSelect';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'completed', label: 'Completed' },
];

const VendorConsultations = () => {
  const { toast } = useToastContext();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(null);
  const [acceptedMeetLink, setAcceptedMeetLink] = useState(null);
  const [googleMeetLink, setGoogleMeetLink] = useState('');
  const [vendorNotes, setVendorNotes] = useState('');

  const params = statusFilter ? { status: statusFilter } : {};
  const { data: consultationsData, isLoading } = useQuery({
    queryKey: ['vendorConsultations', params],
    queryFn: () => getConsultations(params),
  });

  const consultations = consultationsData?.data?.data || consultationsData?.data || [];
  const pagination = consultationsData?.data;

  const acceptMutation = useMutation({
    mutationFn: ({ id, data }) => acceptConsultation(id, data),
    onSuccess: (res) => {
      const link = res?.data?.google_meet_link;
      if (link) {
        setAcceptedMeetLink(link);
      } else {
        toast.success(null, 'Consultation accepted. Meet link will be sent to client.');
        closeAcceptModal();
      }
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Failed to accept consultation');
    },
    onSettled: () => setAcceptingId(null),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, data }) => rejectConsultation(id, data),
    onSuccess: () => {
      toast.success(null, 'Consultation rejected');
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Failed to reject consultation');
    },
    onSettled: () => setRejectingId(null),
  });

  const completeMutation = useMutation({
    mutationFn: (id) => completeConsultation(id),
    onSuccess: () => {
      toast.success(null, 'Consultation marked as completed');
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Failed to complete consultation');
    },
    onSettled: () => setCompletingId(null),
  });

  const handleAccept = (consultation, useAutoGenerate = false) => {
    if (!useAutoGenerate && !googleMeetLink.trim()) {
      toast.error(null, 'Please enter the Google Meet link or use Generate Meet Link');
      return;
    }
    setAcceptingId(consultation.id);
    acceptMutation.mutate({
      id: consultation.id,
      data: useAutoGenerate
        ? { auto_generate: true, vendor_notes: vendorNotes || null }
        : { google_meet_link: googleMeetLink.trim(), vendor_notes: vendorNotes || null },
    });
  };

  const closeAcceptModal = () => {
    setShowAcceptModal(null);
    setAcceptedMeetLink(null);
    setGoogleMeetLink('');
    setVendorNotes('');
  };

  const handleCopyLink = () => {
    if (acceptedMeetLink) {
      navigator.clipboard.writeText(acceptedMeetLink);
      toast.success(null, 'Link copied to clipboard');
    }
  };

  const handleComplete = (consultation) => {
    if (!window.confirm('Mark this consultation as completed?')) return;
    setCompletingId(consultation.id);
    completeMutation.mutate(consultation.id);
  };

  const handleReject = (consultation) => {
    if (!window.confirm('Are you sure you want to reject this consultation request?')) return;
    setRejectingId(consultation.id);
    rejectMutation.mutate({ id: consultation.id, data: {} });
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD');
  const formatTime = (t) => {
    if (!t) return 'TBD';
    const parts = String(t).split(':');
    const h = parseInt(parts[0] || 0, 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${m} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading consultations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Consultations</h1>
          <p className="text-gray-600 mt-1">View and manage consultation requests from clients</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="w-48">
              <CustomSelect
                label="Status"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All statuses"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {consultations.length === 0 ? (
            <div className="p-12 text-center">
              <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Consultations</h3>
              <p className="text-gray-600">
                {statusFilter
                  ? 'No consultations match the selected filter.'
                  : 'Consultation requests from clients will appear here when they use the "Book Consultation" button on your service pages.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {consultations.map((c) => (
                <div key={c.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {c.client?.name || 'Unknown Client'}
                        </h3>
                        {getStatusBadge(c.status)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(c.scheduled_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTime(c.scheduled_time)}
                        </span>
                        {c.service?.service_name && (
                          <span>{c.service.service_name}</span>
                        )}
                      </div>
                      {c.client?.email && (
                        <p className="text-xs text-gray-500 mt-1">{c.client.email}</p>
                      )}
                      {c.questionnaire_responses && Object.keys(c.questionnaire_responses).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-sm text-indigo-600 cursor-pointer hover:underline">
                            View client responses
                          </summary>
                          <ul className="mt-2 pl-4 text-sm text-gray-600 space-y-1">
                            {Object.entries(c.questionnaire_responses).map(([k, v]) =>
                              v ? <li key={k}>{v}</li> : null
                            )}
                          </ul>
                        </details>
                      )}
                    </div>
                    {c.status === 'pending' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => setShowAcceptModal(c)}
                          disabled={acceptingId || rejectingId}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(c)}
                          disabled={acceptingId || rejectingId}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                    {c.status === 'accepted' && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {c.google_meet_link && (
                          <a
                            href={c.google_meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open Meet
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleComplete(c)}
                          disabled={completingId || acceptingId || rejectingId}
                          loading={completeMutation.isPending && completingId === c.id}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Completed
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Accept Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={closeAcceptModal} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              {acceptedMeetLink ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Consultation Accepted</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Share this Google Meet link with your client. You can also send it via email.
                  </p>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                    <a
                      href={acceptedMeetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-sm text-indigo-600 hover:underline truncate"
                    >
                      {acceptedMeetLink}
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyLink}
                      title="Copy link"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button variant="primary" className="w-full" onClick={closeAcceptModal}>
                    Done
                  </Button>
                </>
              ) : (
                <>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Accept Consultation</h3>
              <p className="text-sm text-gray-600 mb-4">
                Generate a Google Meet link automatically or add your own. The client will receive it via email.
              </p>
              <div className="space-y-4">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => handleAccept(showAcceptModal, true)}
                  loading={acceptMutation.isPending}
                  disabled={acceptingId}
                >
                  <Video className="w-4 h-4 mr-2" />
                  Generate Meet Link & Accept
                </Button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-gray-500">or add manually</span>
                  </div>
                </div>
                <Input
                  label="Google Meet Link"
                  type="url"
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  value={googleMeetLink}
                  onChange={(e) => setGoogleMeetLink(e.target.value)}
                />
                <Input
                  label="Notes (optional)"
                  placeholder="Any notes for the client"
                  value={vendorNotes}
                  onChange={(e) => setVendorNotes(e.target.value)}
                />
              </div>
              <div className="flex gap-2 mt-6 justify-end">
                <Button variant="outline" onClick={closeAcceptModal}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleAccept(showAcceptModal, false)}
                  loading={acceptMutation.isPending}
                  disabled={!googleMeetLink.trim() || acceptMutation.isPending}
                >
                  Accept & Send Link
                </Button>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorConsultations;
